import { FC } from 'react';
import { useTranslation, Trans } from 'next-i18next';
import {
  APP_STORE_APPLE,
  APP_STORE_GOOGLE,
  BOATING_EDUCATION_LINKS,
  GMB_SUPPORT_EMAIL,
  SERVICE_FEES,
  SUPPORT_URLS,
} from 'src/common/constants';
import PitchPageLayout from 'src/common/components/PitchPageLayout';
import PitchPageFAQ from 'src/common/components/PitchPageFAQ';
import Typography from 'src/common/components/Typography';
import UniversalLink from 'src/common/components/UniversalLink';

const faqList0 = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
const faqList1 = [0, 1];
const faqList2 = [0, 1, 2];

const FAQs: FC = () => {
  const [t] = useTranslation('for-owners-page');

  return (
    <PitchPageLayout backgroundColor="ivory">
      <PitchPageFAQ
        additionalText={(
          <Trans
            t={t}
            i18nKey="faq--text"
            components={{
              helpCenter: (
                <UniversalLink
                  href={SUPPORT_URLS.HELP_CENTER}
                  openInNewTab
                />
              ),
              break: (
                <br />
              ),
            }}
          />
        )}
        faqs={[
          {
            question: t('faq--question-0'),
            answer: (
              <>
                <Typography type="p" size="body-l">
                  {t('faq--answer-0--p-0')}
                </Typography>
                <ul>
                  {faqList0.map((item) => (
                    <li key={item}>
                      <Typography type="p" size="body-l">
                        <Trans
                          t={t}
                          i18nKey={`faq--answer-0--list-0--listItem-${item}`}
                          components={{
                            iOSLink: (
                              <UniversalLink
                                href={APP_STORE_APPLE}
                                openInNewTab
                              />
                            ),
                            androidLink: (
                              <UniversalLink
                                href={APP_STORE_GOOGLE}
                                openInNewTab
                              />
                            ),
                          }}
                        />
                      </Typography>
                    </li>
                  ))}
                </ul>
                <Typography type="p" size="body-l">
                  <Trans
                    t={t}
                    i18nKey="faq--answer-0--p-1"
                    components={{
                      learnMore: (
                        <UniversalLink
                          href={SUPPORT_URLS.WHAT_GMB_OFFERS}
                          openInNewTab
                        />
                      ),
                    }}
                  />
                </Typography>
              </>
            ),
          },
          {
            question: t('faq--question-1'),
            answer: (
              <>
                <Typography type="p" size="body-l">
                  {t('faq--answer-1--p-0')}
                </Typography>
                <Typography type="p" size="body-l">
                  {t('faq--answer-1--p-1')}
                </Typography>
                <ul>
                  {faqList1.map((item) => (
                    <li key={item}>
                      <Typography type="p" size="body-l">
                        {t(`faq--answer-1--list-0--listItem-${item}`)}
                      </Typography>
                    </li>
                  ))}
                </ul>
                <Typography type="p" size="body-l">
                  {t('faq--answer-1--p-2')}
                </Typography>
                <ul>
                  {faqList2.map((item) => (
                    <li key={item}>
                      <Typography type="p" size="body-l">
                        {t(`faq--answer-1--list-1--listItem-${item}`)}
                      </Typography>
                    </li>
                  ))}
                </ul>
                <Typography type="p" size="body-l">
                  {t('faq--answer-1--p-3')}
                </Typography>
                <Typography type="p" size="body-l">
                  <Trans
                    t={t}
                    i18nKey="faq--answer-1--p-4"
                    values={{ supportEmail: GMB_SUPPORT_EMAIL }}
                    components={{
                      emailLink: (
                        <UniversalLink
                          href={`mailto:${GMB_SUPPORT_EMAIL}`}
                          openInNewTab
                        />
                      ),
                    }}
                  />
                </Typography>
              </>
            ),
          },
          {
            question: t('faq--question-2'),
            answer: (
              <>
                <Typography type="p" size="body-l">
                  {t('faq--answer-2--p-0', {
                    ownerPercent: SERVICE_FEES.OWNER_PERCENT,
                    internationalOwnerPercent: SERVICE_FEES.INTERNATIONAL_OWNER_PERCENT,
                    internationalTransactionFee: SERVICE_FEES.INTERNATIONAL_TRANSACTION_FEE,
                  })}
                </Typography>
                <Typography type="p" size="body-l">
                  <Trans
                    t={t}
                    i18nKey="faq--answer-2--p-1"
                    values={{
                      transactionFee: SERVICE_FEES.TRANSACTION_FEE,
                      internationalTransactionFee: SERVICE_FEES.INTERNATIONAL_TRANSACTION_FEE,
                    }}
                    components={{
                      chargeLink: (
                        <UniversalLink
                          href={SUPPORT_URLS.WHAT_IS_GMB_CHARGE}
                          openInNewTab
                        />
                      ),
                    }}
                  />
                </Typography>
              </>
            ),
          },
          {
            question: t('faq--question-3'),
            answer: (
              <>
                <Typography type="p" size="body-l">
                  <Trans
                    t={t}
                    i18nKey="faq--answer-3--p-0"
                    components={{
                      lawsLink: (
                        <UniversalLink
                          href={BOATING_EDUCATION_LINKS.FEDERAL_LAWS}
                          openInNewTab
                        />
                      ),
                      regulationsLink: (
                        <UniversalLink
                          href={BOATING_EDUCATION_LINKS.FEDERAL_REGULATIONS}
                          openInNewTab
                        />
                      ),
                      rulesLink: (
                        <UniversalLink
                          href={BOATING_EDUCATION_LINKS.NAVIGATION_RULES}
                          openInNewTab
                        />
                      ),
                      boatEd: (
                        <UniversalLink
                          href={BOATING_EDUCATION_LINKS.BOAT_ED}
                          openInNewTab
                        />
                      ),
                    }}
                  />
                </Typography>
                <Typography type="p" size="body-l">
                  <Trans
                    t={t}
                    i18nKey="faq--answer-3--p-1"
                    components={{
                      stateLawsLink: (
                        <UniversalLink
                          href={SUPPORT_URLS.US_BOATING_REQUIREMENTS_BY_STATE}
                          openInNewTab
                        />
                      ),
                    }}
                  />
                </Typography>
              </>
            ),
          },
        ]}
        title={t('faq--title')}
      />
    </PitchPageLayout>
  );
};

export default FAQs;
